import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantsApi } from '@/lib/api'
import { ArrowLeft, Building2 } from 'lucide-react'

const TIERS = ['free', 'basic', 'professional', 'enterprise']

export default function TenantFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    max_users: 10,
    max_devices: 10,
    subscription_tier: 'basic',
    is_active: true,
  })
  const [error, setError] = useState<string | null>(null)

  // Fetch existing tenant when in edit mode
  const { data: tenantData, isLoading: isFetching } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => tenantsApi.get(Number(id)),
    enabled: isEdit,
  })

  useEffect(() => {
    if (tenantData?.data) {
      const t = tenantData.data
      setForm({
        name: t.name ?? '',
        slug: t.slug ?? '',
        description: t.description ?? '',
        max_users: t.max_users ?? 10,
        max_devices: t.max_devices ?? 10,
        subscription_tier: t.subscription_tier ?? 'basic',
        is_active: t.is_active ?? true,
      })
    }
  }, [tenantData])

  const createMut = useMutation({
    mutationFn: () => tenantsApi.create(form as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      navigate('/tenants')
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail ?? err?.response?.data?.error ?? 'Erro ao criar tenant')
    },
  })

  const updateMut = useMutation({
    mutationFn: () => tenantsApi.update(Number(id), form as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      queryClient.invalidateQueries({ queryKey: ['tenant', id] })
      navigate('/tenants')
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail ?? err?.response?.data?.error ?? 'Erro ao guardar tenant')
    },
  })

  const isPending = createMut.isPending || updateMut.isPending
  const handleSubmit = () => isEdit ? updateMut.mutate() : createMut.mutate()

  const autoSlug = (name: string) =>
    name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

  const set = (key: string, val: any) => setForm(f => ({ ...f, [key]: val }))

  if (isEdit && isFetching) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/tenants')} className="rounded p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary-600" />
          {isEdit ? 'Editar Tenant' : 'Novo Tenant'}
        </h1>
      </div>

      <div className="card space-y-5">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome da organização *</label>
            <input className="input w-full" placeholder="Ex: Aquinos Industrial"
              value={form.name}
              onChange={e => {
                const name = e.target.value
                set('name', name)
                if (!form.slug || form.slug === autoSlug(form.name)) {
                  set('slug', autoSlug(name))
                }
              }} />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug (identificador único) *</label>
            <input className="input w-full font-mono text-sm" placeholder="ex: aquinos-industrial"
              value={form.slug}
              onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
            <p className="mt-1 text-xs text-gray-400">Apenas letras minúsculas, números e hífens</p>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea className="input w-full resize-none" rows={2} placeholder="Descrição opcional..."
              value={form.description}
              onChange={e => set('description', e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
            <select className="input w-full" value={form.subscription_tier}
              onChange={e => set('subscription_tier', e.target.value)}>
              {TIERS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max utilizadores</label>
            <input type="number" min={1} max={9999} className="input w-full"
              value={form.max_users}
              onChange={e => set('max_users', parseInt(e.target.value) || 1)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max dispositivos</label>
            <input type="number" min={1} max={9999} className="input w-full"
              value={form.max_devices}
              onChange={e => set('max_devices', parseInt(e.target.value) || 1)} />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input type="checkbox" id="is_active" checked={form.is_active}
              onChange={e => set('is_active', e.target.checked)} />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Tenant ativo</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t">
          <button className="btn btn-secondary" onClick={() => navigate('/tenants')}>Cancelar</button>
          <button className="btn btn-primary"
            disabled={!form.name || !form.slug || isPending}
            onClick={handleSubmit}>
            {isPending ? (isEdit ? 'A guardar...' : 'A criar...') : (isEdit ? 'Guardar Alterações' : 'Criar Tenant')}
          </button>
        </div>
      </div>
    </div>
  )
}

